import {
  CORE_SCALE_SECTIONS,
  ROBUST_ASSESSMENT_MODEL,
  ROBUST_ASSESSMENT_SCHEMA_VERSION,
} from "@/components/assessment/robustAssessmentData";
import {
  buildRobustNervousSystemProfile,
  shouldAskCoachingResponse,
  shouldAskConflictCarryover,
  shouldAskSharedActivity,
} from "@/components/assessment/robustAssessmentEngine";

const SCORE_BANDS = {
  low: [1, 2],
  mid: [3],
  high: [4, 5],
};

const RECOVERY_DURATION_BY_BAND = {
  low: ["day", "next-day"],
  mid: ["hours"],
  high: ["minutes", "hour"],
};

const MOVEMENT_DAYS_BY_BAND = {
  low: ["one-two"],
  mid: ["three-four"],
  high: ["five-six", "daily"],
};

const POSITIVE_RECENT_PATTERNS = [
  "more calm or steady",
  "more positive or energized",
];

const DIFFICULTY_SHIFTING_ATTENTION = "difficulty shifting attention";

const EXPRESSIONS = {
  activated: {
    emotions: ["angry or irritable", "anxious or overwhelmed", "restless or activated"],
    bodySignals: ["breathing changes", "heart or pulse becomes noticeable", "muscle tension", "restlessness or pacing"],
    cognitiveSignals: ["racing thoughts", "tunnel vision", "difficulty shifting attention"],
    reactionDirections: ["move or take action", "solve or focus on the problem", "leave or create space"],
  },
  narrowed: {
    emotions: ["angry or irritable", "anxious or overwhelmed", "mixed or difficult to describe"],
    bodySignals: ["muscle tension", "heat or sweating", "shakiness"],
    cognitiveSignals: ["tunnel vision", "repetitive thoughts", "difficulty switching tasks", DIFFICULTY_SHIFTING_ATTENTION],
    reactionDirections: ["confront or approach", "leave or create space", "seek stimulation or distraction"],
  },
  depleted: {
    emotions: ["sad or low", "numb or withdrawn", "depleted"],
    bodySignals: ["heaviness", "fatigue", "stomach or digestive changes"],
    cognitiveSignals: ["mental fog", "indecision", "losing track of what I was doing"],
    reactionDirections: ["withdraw or disengage", "freeze, stop, or become still", "seek connection or support"],
  },
  environmental: {
    emotions: ["anxious or overwhelmed", "restless or activated", "mixed or difficult to describe"],
    bodySignals: ["sensitivity to noise, light, or touch", "muscle tension", "breathing changes"],
    cognitiveSignals: ["difficulty shifting attention", "mental fog", "tunnel vision"],
    reactionDirections: ["leave or create space", "continue functioning while feeling activated internally", "seek connection or support"],
  },
  flexible: {
    emotions: ["calm or steady", "positive or excited"],
    bodySignals: ["breathing changes", "heart or pulse becomes noticeable"],
    cognitiveSignals: ["hyperfocus", "difficulty shifting attention"],
    reactionDirections: ["solve or focus on the problem", "move or take action", "seek connection or support"],
  },
  mixed: {
    emotions: ["mixed or difficult to describe", "anxious or overwhelmed", "calm or steady"],
    bodySignals: ["muscle tension", "fatigue", "breathing changes"],
    cognitiveSignals: ["repetitive thoughts", "mental fog", "difficulty switching tasks"],
    reactionDirections: ["continue functioning while feeling activated internally", "leave or create space", "seek stimulation or distraction"],
  },
};

const QUICK_RESULT_SCENARIOS = [
  {
    id: "environment-amplified",
    label: "Environment-amplified demand",
    recentChange: "harder",
    expression: "environmental",
    bands: {
      detection: "mid",
      mobilization: "high",
      energy: "high",
      control: "high",
      recovery: "mid",
      environmentDemand: "high",
      environmentControl: "low",
      connection: "mid",
      capacity: "high",
      resources: "mid",
      movement: "high",
    },
  },
  {
    id: "activation-low-energy",
    label: "Strong activation with limited usable energy",
    recentChange: "harder",
    expression: "depleted",
    bands: {
      detection: "mid",
      mobilization: "high",
      energy: "low",
      control: "mid",
      recovery: "mid",
      environmentDemand: "mid",
      environmentControl: "mid",
      connection: "mid",
      capacity: "mid",
      resources: "low",
      movement: "low",
    },
  },
  {
    id: "activation-narrowed-control",
    label: "Strong activation with narrowed control",
    recentChange: "harder",
    expression: "narrowed",
    bands: {
      detection: "mid",
      mobilization: "high",
      energy: "high",
      control: "low",
      recovery: "mid",
      environmentDemand: "mid",
      environmentControl: "mid",
      connection: "low",
      capacity: "high",
      resources: "mid",
      movement: "high",
    },
  },
  {
    id: "activation-recovery-limited",
    label: "Strong activation with recovery limits",
    recentChange: "mixed",
    expression: "activated",
    bands: {
      detection: "high",
      mobilization: "high",
      energy: "high",
      control: "high",
      recovery: "low",
      environmentDemand: "mid",
      environmentControl: "mid",
      connection: "mid",
      capacity: "high",
      resources: "low",
      movement: "high",
    },
  },
  {
    id: "activation-organized",
    label: "Strong activation with retained control",
    recentChange: "easier",
    expression: "activated",
    bands: {
      detection: "high",
      mobilization: "high",
      energy: "high",
      control: "high",
      recovery: "high",
      environmentDemand: "mid",
      environmentControl: "high",
      connection: "high",
      capacity: "high",
      resources: "high",
      movement: "high",
    },
  },
  {
    id: "slow-mobilization",
    label: "Slower mobilization with available capacity",
    recentChange: "mixed",
    expression: "depleted",
    bands: {
      detection: "mid",
      mobilization: "low",
      energy: "mid",
      control: "mid",
      recovery: "mid",
      environmentDemand: "mid",
      environmentControl: "mid",
      connection: "mid",
      capacity: "high",
      resources: "mid",
      movement: "mid",
    },
  },
  {
    id: "slow-recovery",
    label: "Longer recovery after demand",
    recentChange: "harder",
    expression: "mixed",
    bands: {
      detection: "mid",
      mobilization: "mid",
      energy: "mid",
      control: "mid",
      recovery: "low",
      environmentDemand: "mid",
      environmentControl: "mid",
      connection: "mid",
      capacity: "mid",
      resources: "mid",
      movement: "mid",
    },
  },
  {
    id: "reliable-flexibility",
    label: "Reliable nervous-system flexibility",
    recentChange: "easier",
    expression: "flexible",
    bands: {
      detection: "high",
      mobilization: "mid",
      energy: "high",
      control: "high",
      recovery: "high",
      environmentDemand: "low",
      environmentControl: "high",
      connection: "high",
      capacity: "high",
      resources: "high",
      movement: "high",
    },
  },
  {
    id: "variable-context",
    label: "Variable, context-dependent response",
    recentChange: "mixed",
    expression: "mixed",
    bands: {
      detection: "mid",
      mobilization: "mid",
      energy: "mid",
      control: "mid",
      recovery: "mid",
      environmentDemand: "mid",
      environmentControl: "mid",
      connection: "mid",
      capacity: "mid",
      resources: "mid",
      movement: "mid",
    },
  },
];

export const QUICK_ASSESSMENT_SCENARIOS = QUICK_RESULT_SCENARIOS.map(
  ({ id, label }) => ({ id, label }),
);

const normalizeSeed = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 1;
  return (Math.abs(Math.trunc(number)) >>> 0) || 1;
};

const seededRandom = (seed) => {
  let state = normalizeSeed(seed);
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
};

const pick = (random, values) =>
  values[Math.min(values.length - 1, Math.floor(random() * values.length))];

const shuffledSubset = (random, values, minimum = 1, maximum = 3) => {
  const pool = [...values];
  for (let index = pool.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1));
    [pool[index], pool[target]] = [pool[target], pool[index]];
  }
  const count = minimum + Math.floor(random() * (maximum - minimum + 1));
  return pool.slice(0, Math.min(count, pool.length));
};

const score = (random, bandName) => pick(random, SCORE_BANDS[bandName] || SCORE_BANDS.mid);

const inverseBand = (bandName) => {
  if (bandName === "high") return "low";
  if (bandName === "low") return "high";
  return "mid";
};

const scaleAnswers = (sectionId, scenario, random) => {
  const section = CORE_SCALE_SECTIONS.find((item) => item.id === sectionId);
  return Object.fromEntries(
    (section?.questions || []).map((question) => {
      let bandName = scenario.bands[sectionId] || "mid";
      if (sectionId === "mobilization") {
        if (["stateDetection", "interoceptiveClarity"].includes(question.key)) {
          bandName = scenario.bands.detection;
        } else if (question.key === "availableEnergy") {
          bandName = scenario.bands.energy;
        } else {
          bandName = scenario.bands.mobilization;
        }
      }
      if (sectionId === "control") {
        if (["behavioralControl", "stopChangeControl"].includes(question.key)) {
          bandName = scenario.bands.control;
        } else if (question.key === "cognitiveCapture") {
          bandName = inverseBand(scenario.bands.control);
        } else if (question.key === "mobilizationCapacity") {
          bandName = scenario.bands.energy;
        } else if (question.key === "recoveryInitiation") {
          bandName = scenario.bands.recovery;
        }
      }
      return [question.key, score(random, bandName)];
    }),
  );
};

const recentContext = (scenario, random) => {
  const activated = scenario.bands.mobilization === "high";
  const depleted =
    scenario.bands.energy === "low" ||
    scenario.bands.resources === "low" ||
    scenario.expression === "depleted";
  const environment = scenario.bands.environmentDemand === "high";
  const negativePatterns = environment
    ? ["more anxious or overwhelmed", "more difficulty focusing or shifting attention", "slower recovery after stress or activity"]
    : depleted
      ? ["lower physical energy or greater depletion", "more sad or emotionally low", "slower recovery after stress or activity"]
      : activated
        ? ["more alert, tense, or physically activated", "more restless or unable to settle", "more difficulty focusing or shifting attention"]
        : ["more anxious or overwhelmed", "more difficulty focusing or shifting attention", "slower recovery after stress or activity"];
  const recentPatterns = scenario.recentChange === "easier"
    ? shuffledSubset(random, POSITIVE_RECENT_PATTERNS, 2, 2)
    : scenario.recentChange === "mixed"
      ? [
          pick(random, POSITIVE_RECENT_PATTERNS),
          ...shuffledSubset(random, negativePatterns, 1, 2),
        ]
      : shuffledSubset(random, negativePatterns, 2, 3);
  return {
    recentChange: scenario.recentChange,
    recentPatterns,
    changeTiming: pick(random, ["two-weeks", "two-four-weeks", "one-three-months"]),
    helpfulFactors: shuffledSubset(
      random,
      [
        "walking or easy movement",
        "breathing or somatic practice",
        "outdoor time or natural light",
        "structured routine",
        "improved sleep or rest",
        "hydration or nourishment",
        "1:1 coaching",
      ],
      2,
      4,
    ),
    worseningFactors: shuffledSubset(
      random,
      environment
        ? ["crowding, noise, screens, or interruptions", "unpredictable schedules", "travel or transitions between locations", "several demands occurring at once"]
        : depleted
          ? ["poor or disrupted sleep", "missed meals, low intake, or low hydration", "heavy work, school, or caregiving demand", "pain, illness, or physical limitation"]
          : ["interpersonal conflict", "competition or performance pressure", "unpredictable schedules", "several demands occurring at once"],
      2,
      3,
    ),
    context: "Temporary randomized owner preview generated for results testing.",
  };
};

const expressionAnswers = (scenario, random) => {
  const expression = EXPRESSIONS[scenario.expression] || EXPRESSIONS.mixed;
  const otherCognitiveSignals = expression.cognitiveSignals.filter(
    (item) => item !== DIFFICULTY_SHIFTING_ATTENTION,
  );
  const cognitiveSignals = scenario.bands.control === "low"
    ? [
        DIFFICULTY_SHIFTING_ATTENTION,
        ...shuffledSubset(random, otherCognitiveSignals, 1, 2),
      ]
    : shuffledSubset(random, otherCognitiveSignals, 2, 3);

  return {
    emotions: shuffledSubset(random, expression.emotions, 2, 3),
    bodySignals: shuffledSubset(random, expression.bodySignals, 2, 3),
    cognitiveSignals,
    reactionDirections: shuffledSubset(random, expression.reactionDirections, 1, 2),
  };
};

const environmentAnswers = (scenario, random) => {
  const difficult = scenario.bands.environmentDemand === "high"
    ? ["noisy environments", "screens and notifications", "frequent interruptions", "crowded environments or limited personal space"]
    : ["unpredictable schedules", "frequent interruptions", "travel or commuting", "screens and notifications"];
  return {
    difficultEnvironments: shuffledSubset(random, difficult, 2, 3),
    effectMagnitude: score(random, scenario.bands.environmentDemand),
    environmentalControl: score(random, scenario.bands.environmentControl),
    helpfulEnvironments: shuffledSubset(
      random,
      ["quiet or private space", "outdoor space or natural light", "familiar environment", "predictable or structured environment", "movement or exercise setting"],
      2,
      3,
    ),
  };
};

const connectionAnswers = (scenario, random) => {
  const settings = scenario.bands.connection === "high"
    ? ["1:1 coaching", "with a close friend, partner, or family member", "small group"]
    : scenario.bands.connection === "low"
      ? ["alone"]
      : ["alone", "1:1 coaching", "small group"];
  return {
    helpfulSettings: shuffledSubset(random, settings, 1, 2),
    supportAvailability: score(random, scenario.bands.connection),
    socialEvaluation: score(random, inverseBand(scenario.bands.connection)),
  };
};

const activityAnswers = (scenario, random) => {
  const lowCapacity = scenario.bands.capacity === "low";
  const pool = lowCapacity
    ? ["walking or easy movement", "stretching or mobility", "physiotherapy or rehabilitation"]
    : ["walking or easy movement", "aerobic or cardio exercise", "strength training", "sport or recreation", "group classes or team activity", "1:1 coaching"];
  const currentActivities = shuffledSubset(random, pool, 1, lowCapacity ? 2 : 3);
  const familiarActivities = shuffledSubset(random, currentActivities, 1, currentActivities.length);
  const preserveActivities = shuffledSubset(random, familiarActivities, 1, familiarActivities.length);
  return {
    currentActivities,
    movementDays: pick(random, MOVEMENT_DAYS_BY_BAND[scenario.bands.movement]),
    familiarActivities,
    preserveActivities,
  };
};

const completeConditionalAnswers = (answers, scenario, random) => {
  const next = {
    ...answers,
    connection: { ...answers.connection },
  };
  if (shouldAskCoachingResponse(next)) {
    next.connection.coachingResponse = score(random, scenario.bands.connection);
  }
  if (shouldAskConflictCarryover(next)) {
    next.connection.conflictCarryover = pick(
      random,
      RECOVERY_DURATION_BY_BAND[scenario.bands.recovery],
    );
  }
  if (shouldAskSharedActivity(next)) {
    next.connection.sharedActivityBenefit = score(random, scenario.bands.connection);
  }
  return next;
};

const buildAnswers = (scenario, random) => {
  const activity = activityAnswers(scenario, random);
  const answers = {
    meta: {
      assessmentSchemaVersion: ROBUST_ASSESSMENT_SCHEMA_VERSION,
      assessmentModel: ROBUST_ASSESSMENT_MODEL,
    },
    recent: recentContext(scenario, random),
    mobilization: scaleAnswers("mobilization", scenario, random),
    control: {
      ...scaleAnswers("control", scenario, random),
      recoveryDuration: pick(
        random,
        RECOVERY_DURATION_BY_BAND[scenario.bands.recovery],
      ),
    },
    expression: expressionAnswers(scenario, random),
    environment: environmentAnswers(scenario, random),
    connection: connectionAnswers(scenario, random),
    capacity: scaleAnswers("capacity", scenario, random),
    resources: scaleAnswers("resources", scenario, random),
    activity,
    schedule: {
      scheduledActivities: [],
      preferredActivities: [...activity.currentActivities],
      familiarActivities: [...activity.familiarActivities],
    },
  };
  return completeConditionalAnswers(answers, scenario, random);
};

export function createQuickAssessmentSeed(previousSeed = null) {
  let value;
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    const data = new Uint32Array(1);
    globalThis.crypto.getRandomValues(data);
    value = data[0];
  } else {
    value = (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0;
  }
  const seed = normalizeSeed(value);
  return seed === normalizeSeed(previousSeed) ? normalizeSeed(seed + 0x9e3779b9) : seed;
}

export function generateQuickAssessmentPreview({
  seed = createQuickAssessmentSeed(),
  scenarioId = null,
  excludeScenarioId = null,
} = {}) {
  const normalizedSeed = normalizeSeed(seed);
  const random = seededRandom(normalizedSeed);
  const explicitScenario = scenarioId
    ? QUICK_RESULT_SCENARIOS.find((item) => item.id === scenarioId)
    : null;
  const available = excludeScenarioId
    ? QUICK_RESULT_SCENARIOS.filter((item) => item.id !== excludeScenarioId)
    : QUICK_RESULT_SCENARIOS;
  const scenario = explicitScenario || pick(random, available);
  const answers = buildAnswers(scenario, random);
  return {
    seed: normalizedSeed,
    scenarioId: scenario.id,
    scenarioLabel: scenario.label,
    answers,
    profile: buildRobustNervousSystemProfile(answers),
  };
}
